
import React from "react";
import BlogCard from "@/components/BlogCard";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import { STATIC_ROUTE_META } from "@/utils/routeMetadata";
import SeasonalResources from "@/components/sections/SeasonalResources";

const Blog = () => {
  const { posts, isLoading, error } = useBlogPosts({ 
    published: true,
    orderBy: "created_at:desc" 
  });

  useSEO(STATIC_ROUTE_META["/blog"]);

  if (isLoading) {
    return (
      <div>
        <section className="py-16 md:py-24 bg-elfGreen-light">
          <div className="container mx-auto px-4">
            <nav className="mb-6 text-sm" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-elfGreen-dark hover:underline">Home</Link></li>
                <li className="flex items-center">
                  <span className="mx-2">/</span>
                  <span className="font-medium">Blog</span>
                </li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Elf Letter Bot Blog
            </h1>
            <p className="text-lg md:text-xl mb-12 text-center max-w-3xl mx-auto">
              Discover magical ideas, tips, and inspiration for your family's Christmas elf adventures.
            </p>
            
            <SeasonalResources embedded />

            <div className="flex justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-elfGreen-dark mx-auto mb-4"></div>
                <p className="text-lg">Loading blog posts...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <section className="py-16 md:py-24 bg-elfGreen-light">
          <div className="container mx-auto px-4">
            <nav className="mb-6 text-sm" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-elfGreen-dark hover:underline">Home</Link></li>
                <li className="flex items-center">
                  <span className="mx-2">/</span>
                  <span className="font-medium">Blog</span>
                </li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Elf Letter Bot Blog
            </h1>
            <p className="text-lg md:text-xl mb-12 text-center max-w-3xl mx-auto">
              Discover magical ideas, tips, and inspiration for your family's Christmas elf adventures.
            </p>
            
            <SeasonalResources embedded />

            <div className="text-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-lg text-red-600 font-medium mb-2">Unable to load blog posts</p>
                <p className="text-sm text-red-500">{error.message}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div>
        <section className="py-16 md:py-24 bg-elfGreen-light">
          <div className="container mx-auto px-4">
            <nav className="mb-6 text-sm" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-elfGreen-dark hover:underline">Home</Link></li>
                <li className="flex items-center">
                  <span className="mx-2">/</span>
                  <span className="font-medium">Blog</span>
                </li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Elf Letter Bot Blog
            </h1>
            <p className="text-lg md:text-xl mb-12 text-center max-w-3xl mx-auto">
              Discover magical ideas, tips, and inspiration for your family's Christmas elf adventures.
            </p>
            
            <SeasonalResources embedded />

            <div className="text-center py-12">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-lg text-yellow-800 font-medium">No blog posts found</p>
                <p className="text-sm text-yellow-600 mt-2">Posts may need to be marked as "published" in the admin panel.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="py-16 md:py-24 bg-elfGreen-light">
        <div className="container mx-auto px-4">
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li><Link to="/" className="text-elfGreen-dark hover:underline">Home</Link></li>
              <li className="flex items-center">
                <span className="mx-2">/</span>
                <span className="font-medium">Blog</span>
              </li>
            </ol>
          </nav>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
            Elf Letter Bot Blog
          </h1>
          <p className="text-lg md:text-xl mb-12 text-center max-w-3xl mx-auto">
            Discover magical ideas, tips, and inspiration for your family's Christmas elf adventures.
          </p>
          
          <SeasonalResources embedded />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <BlogCard
                key={post.id}
                title={post.title}
                excerpt={post.excerpt}
                slug={post.slug}
                imageSrc={post.featured_image || "https://images.unsplash.com/photo-1607305387299-a3d9611cd469"}
                date={new Date(post.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                isoDate={post.created_at}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
